import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { environment, SETTINGS_KEYS } from '../../config/environment'
import { useAuth } from '../../hooks/useAuth'
import { useSetting } from '../../hooks/useSettings'
import Logo from '../common/Logo'
import './Login.scss'

export const Login: React.FC = () => {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [showMfaCode, setShowMfaCode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { login, completeOrganizationSelection } = useAuth()
  const [availableOrganizations, setAvailableOrganizations] = useState<Array<{ id: number; name: string }>>([])
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null)
  const appTitle = useSetting<string>(SETTINGS_KEYS.APP_TITLE, environment.appTitle)
  const appSubtitle = useSetting<string | null>(SETTINGS_KEYS.APP_SUBTITLE, environment.appSubtitle)
  const showAppLogo = useSetting<boolean>(SETTINGS_KEYS.SHOW_APP_LOGO, true)
  // const showAppLogoIcon = useSetting<boolean>(SETTINGS_KEYS.SHOW_APP_LOGO_ICON, true);
  const showAppTitle = useSetting<boolean>(SETTINGS_KEYS.SHOW_APP_TITLE, true)
  const showAppSubtitle = useSetting<boolean>(SETTINGS_KEYS.SHOW_APP_SUBTITLE, true)
  const resolvedSubtitle = (appSubtitle && appSubtitle.length ? appSubtitle : environment.appSubtitle) || t('sign_in_to_system')

  useEffect(() => {
    document.title = `${t('login')} - ${appTitle}`
  }, [t, appTitle])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    if (availableOrganizations.length > 0 && selectedOrganizationId) {
      try {
        await completeOrganizationSelection(selectedOrganizationId)
      } finally {
        setIsLoading(false)
      }
      return
    }
    const result = await login(username, password, mfaCode)
    setIsLoading(false)

    if (result === 'mfa_required') {
      setShowMfaCode(true)
    } else if (typeof result === 'object' && (result as any).organization_selection_required) {
      const orgs = ((result as any).organizations || []) as Array<{ id: number; name: string }>
      setAvailableOrganizations(orgs)
      setSelectedOrganizationId(orgs.length > 0 ? orgs[0].id : null)
    } else if (!result) {
      if (showMfaCode) {
        setMfaCode('')
      } else {
        setPassword('')
        setMfaCode('')
        setShowMfaCode(false)
      }
    } else {
      setShowMfaCode(false)
    }
  }

  const heroHighlights = [
    { icon: 'bi-kanban', text: t('login_secondary_feature_1') },
    { icon: 'bi-search', text: t('login_secondary_feature_2') },
    { icon: 'bi-shield-check', text: t('login_secondary_feature_3') },
  ]

  const heroStats = [
    { value: '95%+', label: t('login_stat_ai_ocr') },
    { value: '100%', label: t('login_stat_ai_search') },
  ]

  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-panel">
          <div className="login-brand">
            {/* {showAppLogoIcon && (
                <Logo
                  height="2.4rem"
                  className="login-logo"
                  iconOnly
                />
              )} */}
            {showAppLogo && <Logo height="3.1rem" className="login-logo" />}
            <div className="login-brand-titles">
              {showAppTitle && <h1>{appTitle}</h1>}
              {showAppSubtitle && <p>{resolvedSubtitle}</p>}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                <i className="bi bi-person-fill"></i>
                {t('username')}
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  className="form-input"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('enter_username')}
                  required
                  autoFocus
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                <i className="bi bi-lock-fill"></i>
                {t('password')}
              </label>
              <div className="input-wrapper password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('enter_password')}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? t('password') : t('show_password')}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                </button>
              </div>
            </div>

            {showMfaCode && (
              <div className="form-group mfa-group">
                <label htmlFor="mfaCode" className="form-label">
                  <i className="bi bi-shield-lock-fill"></i>
                  {t('mfa_code')}
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="form-input"
                    id="mfaCode"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder={t('enter_mfa_code')}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                  />
                </div>
                <small className="form-hint">{t('mfa_code_help')}</small>
              </div>
            )}

            {availableOrganizations.length > 0 && (
              <div className="form-group">
                <label htmlFor="organization" className="form-label">
                  <i className="bi bi-building"></i>
                  {t('organization')}
                </label>
                <div className="input-wrapper">
                  <select
                    id="organization"
                    className="form-input"
                    value={selectedOrganizationId ?? ''}
                    onChange={(e) => setSelectedOrganizationId(Number(e.target.value))}
                    required
                  >
                    {availableOrganizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <button type="submit" className="login-button" disabled={isLoading || !username || !password || (showMfaCode && !mfaCode)}>
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  {t('logging_in')}
                </>
              ) : (
                <>
                  <span>{t('login')}</span>
                  <i className="bi bi-arrow-right-short"></i>
                </>
              )}
            </button>
          </form>

          <div className="login-links">
            <a href="/privacy-policy" target="_blank" rel="noreferrer">
              {t('login_privacy_policy')}
            </a>
            <a href="/terms-of-service" target="_blank" rel="noreferrer">
              {t('login_terms_of_service')}
            </a>
          </div>
        </div>

        <div className="login-hero">
          <div className="hero-badge">
            <i className="bi bi-stars"></i>
            <span>{t('login_secondary_title')}</span>
          </div>
          <h2>{t('login_cta_title')}</h2>
          <p>{t('login_cta_subtitle')}</p>

          <ul className="hero-features">
            {heroHighlights.map((item) => (
              <li key={item.icon}>
                <span className="icon">
                  <i className={`bi ${item.icon}`}></i>
                </span>
                <span>{item.text}</span>
              </li>
            ))}
          </ul>

          <div className="hero-footer">
            <div className="hero-stats">
              {heroStats.map((item) => (
                <div key={item.value}>
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
